/* eslint-disable max-lines */
import { setup } from 'xstate';

import type { StateActions, StateContext, StateEvents, StateGuards, StateInput } from './types';

const STEPS = [
	'idle',
	'intro',
	'issue',
	'personal-story',
	'chat-app-entry',
	'recommendations',
	'optional-preview',
	'preview',
	'create',
	'edit',
	'location',
	'media',
	'prepublish',
] as const;

export const machine = setup({
	/* eslint-disable @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion */
	types: { context: {} as StateContext, events: {} as StateEvents, input: {} as StateInput },
	actions: {} as StateActions,
	guards: {} as StateGuards,
	/* eslint-enable @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion */
}).createMachine({
	id: 'start-a-petition',
	initial: 'idle',
	context: ({ input }) => ({ ...input }),
	on: {
		sync: STEPS.map((state) => ({
			target: `.${state}`,
			guard: ({ event }) => event.step === state,
		})),
	},
	states: {
		idle: {},
		intro: {
			on: {
				submit: [
					{
						description: 'if user chose the ai driven flow',
						target: 'issue',
						guard: 'isAiDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
					{
						description: 'if user chose the user driven flow',
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
				],
			},
		},
		issue: {
			on: {
				back: [{ target: 'intro', actions: ['unsetDraftType'] }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'personal-story',
						guard: 'isAiDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' } }",
						target: 'personal-story',
						guard: 'isRecommendationsDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant_b', draft: { type: 'recommendations_variant_b_draft' } }",
						target: 'recommendations',
						guard: 'isRecommendationsVariantBDraft',
						actions: ['onSubmit'],
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'personal-story',
						actions: ['onSubmit'],
					},
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		'personal-story': {
			on: {
				back: [
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'issue',
						guard: 'isAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' } }",
						target: 'issue',
						guard: 'isRecommendationsDraft',
					},
					{
						description: "{ experiment: 'variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'recommendations',
						guard: 'isRecommendationsVariantBAiDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'issue',
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'preview',
						guard: 'isAiDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' } }",
						target: 'recommendations',
						guard: 'isRecommendationsDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant_b', draft: { type: 'recommendations_variant_b_draft' } }",
						target: 'preview',
						guard: 'isRecommendationsVariantBDraft',
						actions: ['onSubmit'],
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'preview',
						actions: ['onSubmit'],
					},
					{ target: 'preview' },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		'chat-app-entry': {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		recommendations: {
			on: {
				back: [
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' } }",
						target: 'personal-story',
						guard: 'isRecommendationsDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft' } }",
						target: 'issue',
						guard: 'isRecommendationsVariantBDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
				],
				submit: [
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' }, target: 'ai' }",
						target: 'preview',
						guard: 'isRecommendationsAiDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft' }, target: 'ai' }",
						target: 'personal-story',
						guard: 'isRecommendationsVariantBAiDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
					// user draft
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft' }, target: 'user' }",
						target: 'create',
						guard: 'isRecommendationsUserDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft' }, target: 'user' }",
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		// START: `ai_draft` only steps
		preview: {
			on: {
				back: [
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'personal-story',
						guard: 'isAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_ai' } }",
						target: 'recommendations',
						guard: 'isRecommendationsAiDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'personal-story',
						guard: 'isRecommendationsVariantBAiDraft',
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'personal-story',
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					// -- editing transitions should come before step submissions
					{
						description: 'transition to edit step',
						target: 'edit',
						guard: { type: 'transitionTo', params: { target: 'edit' } },
						actions: ['onSubmit'],
					},
					// -- these should always be last so they don't match on draft.type first
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'location',
						guard: 'isAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_ai' } }",
						target: 'location',
						guard: 'isRecommendationsAiDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'location',
						guard: 'isRecommendationsVariantBAiDraft',
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'location',
					},
				],
			},
		},
		edit: {
			on: {
				back: [
					// -- edit step only fires `submit` events
				],
				submit: [
					// -- edit transitions do not need conditional transitions based on draft.type
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'preview',
						guard: { type: 'transitionTo', params: { target: 'preview' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					{
						description: 'default submit if no target specified',
						target: 'location',
					},
				],
			},
		},
		// END: `ai_draft` only steps
		create: {
			on: {
				back: [
					{
						description: "{ experiment: 'control', draft: { type: 'user_draft' } }",
						target: 'intro',
						guard: 'isUserDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_user' } }",
						target: 'recommendations',
						guard: 'isRecommendationsUserDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_user' } }",
						target: 'recommendations',
						guard: 'isRecommendationsVariantBUserDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
				],
				submit: [
					// -- create transitions do not need conditional transitions based on draft.type
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'optional-preview',
						guard: { type: 'transitionTo', params: { target: 'optional-preview' } },
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					{ target: 'location' },
				],
			},
		},
		'optional-preview': {
			on: {
				back: [
					{
						description: 'optional-preview only goes back to create',
						target: 'create',
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'create',
						guard: { type: 'transitionTo', params: { target: 'create' } },
					},
					{ target: 'location' },
				],
			},
		},
		media: {
			on: {
				back: [
					{
						description: "{ experiment: 'control', draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_ai' } }",
						target: 'edit',
						guard: 'isRecommendationsAiDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'edit',
						guard: 'isRecommendationsVariantBAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsUserDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'edit',
					},
				],
				submit: [
					{
						description: "{ experiment: 'control', draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_ai' } }",
						target: 'edit',
						guard: 'isRecommendationsAiDraft',
						actions: ['onSubmit'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'edit',
						guard: 'isRecommendationsVariantBAiDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsUserDraft',
						actions: ['onSubmit'],
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
						actions: ['onSubmit'],
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'preview',
						actions: ['onSubmit'],
					},
				],
			},
		},
		location: {
			on: {
				back: [
					{
						description: "{ experiment: 'control', draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ experiment: 'control', draft: { type: 'ai_draft' } }",
						target: 'preview',
						guard: 'isAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_ai' } }",
						target: 'preview',
						guard: 'isRecommendationsAiDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_ai' } }",
						target: 'preview',
						guard: 'isRecommendationsVariantBAiDraft',
					},
					{
						description: "{ experiment: 'variant', draft: { type: 'recommendations_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsUserDraft',
					},
					{
						description:
							"{ experiment: 'recommendations_variant_b', draft: { type: 'recommendations_variant_b_draft_user' } }",
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
					},
					{
						description: '{ draft: { type: undefined } }',
						target: 'preview',
					},
				],
				submit: [
					// -- location submit transitions do not need conditional transitions based on draft.type
					{
						target: 'prepublish',
						actions: ['onSubmit'],
					},
				],
			},
		},
		prepublish: {
			on: {
				back: { target: 'location' },
			},
		},
	},
});
